import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Crown, Users, Loader2, Shield, ShieldOff, AlertTriangle, Trash2, Wallet, Search, X, Truck, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { adminAPI } from '@/lib/api';
import { usePagination, TablePagination } from '@/components/admin/TablePagination';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';
import { money } from '@/lib/format';

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [demoting, setDemoting] = useState(false);
  const [vipMarkup, setVipMarkup] = useState(0);
  const [creditUser, setCreditUser] = useState(null);
  const [creditMode, setCreditMode] = useState('add'); // 'add' | 'deduct'
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditTxns, setCreditTxns] = useState([]);
  const [savingCredit, setSavingCredit] = useState(false);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);

  const openCredit = async (u) => {
    setCreditUser(u);
    setCreditMode('add');
    setCreditAmount('');
    setCreditReason('');
    setCreditTxns([]);
    try {
      const res = await adminAPI.creditTransactions(u.id);
      setCreditTxns(res.data.transactions || []);
    } catch { /* ignore */ }
  };

  const submitCredit = async () => {
    const raw = parseFloat(creditAmount);
    if (!raw || raw <= 0) { toast.error('Enter a valid amount'); return; }
    const signed = creditMode === 'deduct' ? -raw : raw;
    setSavingCredit(true);
    try {
      const res = await adminAPI.adjustCredit(creditUser.id, signed, creditReason);
      toast.success(`Credit updated — new balance R${money(Number(res.data.credit_balance))}`);
      setUsers(prev => prev.map(x => x.id === creditUser.id ? { ...x, credit_balance: res.data.credit_balance } : x));
      setCreditUser(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update credit');
    } finally {
      setSavingCredit(false);
    }
  };

  useEffect(() => {
    adminAPI.getSettings()
      .then(r => setVipMarkup(Number(r.data?.vip_markup_percentage ?? 0)))
      .catch(() => {});
  }, []);

  const fetchData = () => {
    setLoading(true);
    adminAPI.listUsers()
      .then(r => setUsers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let active = true;
    adminAPI.listUsers()
      .then(r => { if (active) setUsers(r.data); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const handleToggleVip = async (userId) => {
    setBusy(`vip-${userId}`);
    try {
      const res = await adminAPI.toggleVip(userId);
      toast.success(`VIP status ${res.data.is_vip ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch {
      toast.error('Failed to update VIP status');
    } finally {
      setBusy(null);
    }
  };

  const handleToggleCollectionFeeExempt = async (userId) => {
    setBusy(`cfe-${userId}`);
    try {
      const res = await adminAPI.toggleCollectionFeeExempt(userId);
      toast.success(`Collection fee exemption ${res.data.collection_fee_exempt ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch {
      toast.error('Failed to update collection fee exemption');
    } finally {
      setBusy(null);
    }
  };

  const handleToggleRole = async (u) => {
    const newRole = u.role === 'admin' ? 'user' : 'admin';
    const verb = newRole === 'admin' ? 'PROMOTE' : 'DEMOTE';
    if (!window.confirm(`${verb} ${u.email} to ${newRole.toUpperCase()}?\nThis change is immediate.`)) return;
    setBusy(`role-${u.id}`);
    try {
      await adminAPI.setUserRole(u.id, newRole);
      toast.success(`${u.email} is now ${newRole}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Role update failed');
    } finally {
      setBusy(null);
    }
  };

  const handleDemoteRogues = async () => {
    if (!window.confirm('Demote every user with admin role except yourself and admin@avzwholesale.co.za?')) return;
    setDemoting(true);
    try {
      const res = await adminAPI.demoteRogueAdmins();
      toast.success(`Demoted ${res.data.demoted} rogue admin${res.data.demoted === 1 ? '' : 's'}`);
      fetchData();
    } catch {
      toast.error('Demote failed');
    } finally {
      setDemoting(false);
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently DELETE ${u.email}?\nThis removes the user account AND all of their orders from the database. This cannot be undone.`)) return;
    setBusy(`del-${u.id}`);
    try {
      await adminAPI.deleteUser(u.id);
      toast.success(`${u.email} deleted`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  const adminCount = users.filter(u => u.role === 'admin').length;
  const q = search.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, '');
  const filteredUsers = q
    ? users.filter(u => {
        const phone = (u.cellphone || u.phone || '');
        return (
          (u.name || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          phone.toLowerCase().includes(q) ||
          (qDigits && phone.replace(/\D/g, '').includes(qDigits))
        );
      })
    : users;

  const usersPg = usePagination(filteredUsers, 15);
  const pageUsers = usersPg.pageItems;

  const [exportingUsers, setExportingUsers] = useState(false);
  const handleExportUsers = async () => {
    setExportingUsers(true);
    try {
      const res = await adminAPI.exportUsers();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'AVZ_Customers_Export.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
    finally { setExportingUsers(false); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Customers</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">
            {users.length} users ({adminCount} admin{adminCount === 1 ? '' : 's'} · {users.filter(u => u.is_vip).length} VIP)
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleExportUsers}
            disabled={exportingUsers}
            data-testid="export-customers-btn"
            variant="ctaSecondary"
            size="sm"
            className="rounded-full font-body text-xs gap-1.5"
          >
            {exportingUsers ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={2} />}
            Export customers
          </Button>
          <Button
            onClick={handleDemoteRogues}
            disabled={demoting || adminCount <= 1}
            data-testid="demote-rogue-admins-btn"
            variant="outline"
            size="sm"
            className="rounded-full border-red-700/40 text-red-400 hover:bg-red-900/20 font-body text-xs gap-1.5"
            title="Demote any admin that isn't you or admin@avzwholesale.co.za"
          >
            {demoting ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" strokeWidth={2} />}
            Demote rogue admins
          </Button>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-vintage/10" data-testid="vip-markup-pill">
            <Crown className="h-4 w-4 text-vintage" />
            <span className="font-body text-xs text-vintage font-semibold">
              VIP = {vipMarkup}% markup{vipMarkup === 0 ? ' (cost price)' : ''}
            </span>
          </div>
        </div>
      </div>

      {!loading && (
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
          <Input
            data-testid="user-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, email or cellphone..."
            className="pl-10 h-11 rounded-xl bg-card border-border font-body text-sm"
          />
          {search && (
            <button
              data-testid="user-search-clear"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-16 rounded-2xl bg-muted animate-pulse" />)}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="bg-card border-b border-border">
                <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">User</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Email</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Cellphone</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Role</th>
                <th className="text-center font-body text-xs uppercase tracking-wider text-foreground/60 p-4">VIP</th>
                <th className="text-center font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Collection Fee</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Credit</th>
                <th className="text-left font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Joined</th>
                <th className="text-right font-body text-xs uppercase tracking-wider text-foreground/60 p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center font-body text-sm text-foreground/50" data-testid="users-no-results">
                    No customers match "{search}".
                  </td>
                </tr>
              ) : pageUsers.map(u => {
                const isSelf = currentUser?.id === u.id;
                const isAdmin = u.role === 'admin';
                return (
                  <tr key={u.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-vintage/10 flex items-center justify-center">
                          <Users className="h-4 w-4 text-vintage" />
                        </div>
                        <span className="font-body text-sm font-medium text-foreground">{u.name}</span>
                        {isSelf && <span className="font-body text-[10px] uppercase tracking-wider text-foreground/40">(you)</span>}
                      </div>
                    </td>
                    <td className="p-4 font-body text-sm text-foreground/70">{u.email}</td>
                    <td className="p-4" data-testid={`user-cellphone-${u.id}`}>
                      {(u.cellphone || u.phone)
                        ? <a href={`tel:${u.cellphone || u.phone}`} className="font-body text-sm text-teal hover:underline tabular-nums">{u.cellphone || u.phone}</a>
                        : <span className="font-body text-sm text-foreground/30">—</span>}
                    </td>
                    <td className="p-4">
                      <Badge
                        data-testid={`user-role-${u.id}`}
                        variant="outline"
                        className={`rounded-full font-body text-xs capitalize gap-1 ${
                          isAdmin
                            ? 'border-red-400/40 text-red-400 bg-red-900/10'
                            : 'border-border text-foreground/60'
                        }`}
                      >
                        {isAdmin ? <Shield className="h-3 w-3" strokeWidth={2.5} /> : null}
                        {u.role}
                      </Badge>
                    </td>
                    <td className="p-4 text-center">
                      {u.is_vip ? (
                        <Badge className="bg-gold/15 text-gold border-gold/30 rounded-full font-body text-xs gap-1">
                          <Crown className="h-3 w-3" /> VIP
                        </Badge>
                      ) : (
                        <span className="font-body text-xs text-foreground/40">Standard</span>
                      )}
                    </td>
                    <td className="p-4 text-center" data-testid={`user-cfe-${u.id}`}>
                      {u.collection_fee_exempt ? (
                        <Badge className="bg-teal/15 text-teal border-teal/30 rounded-full font-body text-xs gap-1">
                          <Truck className="h-3 w-3" /> Exempt
                        </Badge>
                      ) : (
                        <span className="font-body text-xs text-foreground/40">Standard</span>
                      )}
                    </td>
                    <td className="p-4 text-right font-body text-sm" data-testid={`user-credit-${u.id}`}>
                      {(u.credit_balance || 0) > 0
                        ? <span className="text-gold font-semibold">R{money(Number(u.credit_balance))}</span>
                        : <span className="text-foreground/40">R0.00</span>}
                    </td>
                    <td className="p-4 font-body text-xs text-foreground/60">
                      {new Date(u.created_at).toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg' })}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openCredit(u)}
                          data-testid={`manage-credit-${u.id}`}
                          className="rounded-full font-body text-xs gap-1 h-8 border-gold/40 text-gold hover:bg-gold/10"
                          title="Add or remove account credit"
                        >
                          <Wallet className="h-3 w-3" strokeWidth={2} /> Credit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleRole(u)}
                          disabled={busy === `role-${u.id}` || isSelf}
                          data-testid={`toggle-role-${u.id}`}
                          className={`rounded-full font-body text-xs gap-1 h-8 ${
                            isAdmin
                              ? 'border-red-700/40 text-red-400 hover:bg-red-900/20'
                              : 'border-teal/30 text-teal hover:bg-teal/10'
                          }`}
                          title={isSelf ? 'You cannot change your own role' : isAdmin ? 'Demote to user' : 'Promote to admin'}
                        >
                          {busy === `role-${u.id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : isAdmin ? (
                            <ShieldOff className="h-3 w-3" strokeWidth={2} />
                          ) : (
                            <Shield className="h-3 w-3" strokeWidth={2} />
                          )}
                          {isAdmin ? 'Demote' : 'Promote'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleVip(u.id)}
                          disabled={busy === `vip-${u.id}`}
                          data-testid={`toggle-vip-${u.id}`}
                          className={`rounded-full font-body text-xs gap-1 h-8 ${
                            u.is_vip
                              ? 'border-destructive/30 text-destructive hover:bg-destructive/10'
                              : 'border-vintage/30 text-vintage hover:bg-vintage/10'
                          }`}
                        >
                          {busy === `vip-${u.id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Crown className="h-3 w-3" />
                          )}
                          {u.is_vip ? 'Remove VIP' : 'Make VIP'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleCollectionFeeExempt(u.id)}
                          disabled={busy === `cfe-${u.id}` || isSelf}
                          data-testid={`toggle-cfe-${u.id}`}
                          className={`rounded-full font-body text-xs gap-1 h-8 ${
                            u.collection_fee_exempt
                              ? 'border-destructive/30 text-destructive hover:bg-destructive/10'
                              : 'border-teal/30 text-teal hover:bg-teal/10'
                          }`}
                          title={u.collection_fee_exempt ? 'Charge the collection fee again' : 'Waive the fixed collection fee for this customer'}
                        >
                          {busy === `cfe-${u.id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Truck className="h-3 w-3" />
                          )}
                          {u.collection_fee_exempt ? 'Charge Fee' : 'Exempt Fee'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(u)}
                          disabled={busy === `del-${u.id}` || isSelf || u.email?.toLowerCase() === 'admin@avzwholesale.co.za'}
                          data-testid={`delete-user-${u.id}`}
                          className="rounded-full font-body text-xs gap-1 h-8 border-red-700/40 text-red-400 hover:bg-red-900/20"
                          title={isSelf ? 'You cannot delete your own account' : u.email?.toLowerCase() === 'admin@avzwholesale.co.za' ? 'The primary admin cannot be deleted' : 'Delete user'}
                        >
                          {busy === `del-${u.id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" strokeWidth={2} />
                          )}
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <TablePagination {...usersPg} testid="users-pagination" />}

      <Dialog open={!!creditUser} onOpenChange={(o) => !o && setCreditUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="credit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light flex items-center gap-2">
              <Wallet className="h-5 w-5 text-gold" /> Account Credit
            </DialogTitle>
          </DialogHeader>
          {creditUser && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-3">
                <p className="font-body text-sm text-foreground">{creditUser.name}</p>
                <p className="font-body text-xs text-foreground/50">{creditUser.email}</p>
                <p className="font-body text-xs text-foreground/60 mt-1">
                  Current balance: <span className="text-gold font-semibold" data-testid="credit-dialog-balance">R{money(Number(creditUser.credit_balance || 0))}</span>
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => setCreditMode('add')}
                  data-testid="credit-mode-add"
                  className={`flex-1 rounded-lg font-body text-xs ${creditMode === 'add' ? 'bg-teal text-white' : 'bg-muted text-foreground/60'}`}
                >Add credit</Button>
                <Button
                  type="button"
                  onClick={() => setCreditMode('deduct')}
                  data-testid="credit-mode-deduct"
                  className={`flex-1 rounded-lg font-body text-xs ${creditMode === 'deduct' ? 'bg-destructive text-white' : 'bg-muted text-foreground/60'}`}
                >Deduct credit</Button>
              </div>
              <div>
                <Label className="font-body text-xs text-foreground/70">Amount (R)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  data-testid="credit-amount-input"
                  value={creditAmount}
                  onChange={e => setCreditAmount(e.target.value)}
                  className="mt-1 rounded-lg" placeholder="0.00"
                />
              </div>
              <div>
                <Label className="font-body text-xs text-foreground/70">Reason (optional)</Label>
                <Input
                  data-testid="credit-reason-input"
                  value={creditReason}
                  onChange={e => setCreditReason(e.target.value)}
                  className="mt-1 rounded-lg" placeholder="e.g. Goodwill gesture"
                />
              </div>
              {creditTxns.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="credit-history">
                  {creditTxns.map((t, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2">
                      <div className="min-w-0">
                        <p className="font-body text-[11px] text-foreground/70 capitalize">{t.type.replace(/_/g, ' ')}</p>
                        <p className="font-body text-[10px] text-foreground/40 truncate">{t.reason || new Date(t.created_at).toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg' })}</p>
                      </div>
                      <span className={`font-body text-xs font-semibold ${t.amount >= 0 ? 'text-teal' : 'text-destructive'}`}>
                        {t.amount >= 0 ? '+' : ''}R{money(Number(t.amount))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditUser(null)} className="rounded-lg font-body text-sm" data-testid="credit-cancel-btn">Cancel</Button>
            <Button
              onClick={submitCredit}
              disabled={savingCredit}
              data-testid="credit-submit-btn"
              className="rounded-lg bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm font-semibold"
            >
              {savingCredit ? <Loader2 className="h-4 w-4 animate-spin" /> : (creditMode === 'add' ? 'Add credit' : 'Deduct credit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
